import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  OnDestroy,
  ViewChild
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { Observable, of, Subscription, timer } from 'rxjs';
import { catchError, exhaustMap } from 'rxjs/operators';
import {
  BusinessImportConfirmRequest,
  BusinessImportConfirmResponse,
  BusinessImportPreviewResponse,
  BusinessImportRow,
  BusinessImportRowValues
} from '../models';
import { BusinessServices } from '../services/business.services';

export type BusinessImportStep = 'upload' | 'preview' | 'complete' | 'failed';
export type BusinessImportField =
  | 'OrganizationNumber'
  | 'CompanyName'
  | 'Email'
  | 'ApplicationScope'
  | 'BusinessCategory'
  | 'Features'
  | 'LicenseCategory'
  | 'PurchaseDate'
  | 'LicenseMonth'
  | 'PricePerMonth'
  | 'HaveEmployees'
  | 'HaveOhc';
type PreviewFilter = 'attention' | 'all' | 'errors' | 'warnings' | 'edited';
type ResultFilter = 'attention' | 'created' | 'all';
type StepState = 'done' | 'current' | 'busy' | 'failed' | 'warn' | 'upcoming';

/** Backend row errors are plain English sentences; each field's messages start with its label. */
const FIELD_PREFIXES: [BusinessImportField, string][] = [
  ['OrganizationNumber', 'organization number'],
  ['CompanyName', 'company name'],
  ['Email', 'email'],
  ['ApplicationScope', 'application scope'],
  ['BusinessCategory', 'business category'],
  ['Features', 'features'],
  ['LicenseCategory', 'license category'],
  ['PurchaseDate', 'purchase date'],
  ['LicenseMonth', 'license month'],
  ['PricePerMonth', 'price per month'],
  ['HaveEmployees', 'have employees'],
  ['HaveOhc', 'have ohc']
];
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ORGANIZATION_NUMBER_PATTERN = /^\d{9,12}$/;
const UNDO_TIMEOUT_MS = 10000;

/**
 * Dedicated page for importing businesses from an XLSX file: upload, review and
 * correct rows, start a durable job, and reopen its saved progress and results.
 */
@Component({
  selector: 'business-import',
  templateUrl: './business-import.component.html',
  styleUrls: ['./business-import.component.css'],
  standalone: false
})
export class BusinessImportComponent implements OnInit, OnDestroy {
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('stepHeading') stepHeading?: ElementRef<HTMLElement>;

  readonly requiredFields: BusinessImportField[] = [
    'OrganizationNumber',
    'CompanyName',
    'Email',
    'ApplicationScope',
    'BusinessCategory',
    'Features'
  ];
  readonly optionalDefaultFields: BusinessImportField[] = [
    'LicenseCategory',
    'PurchaseDate',
    'LicenseMonth',
    'PricePerMonth',
    'HaveEmployees',
    'HaveOhc'
  ];
  readonly fieldLabels: Record<BusinessImportField, string> = {
    OrganizationNumber: 'BUSINESS_IMPORT.HEADERS.ORGANIZATION_NUMBER',
    CompanyName: 'BUSINESS_IMPORT.HEADERS.COMPANY_NAME',
    Email: 'BUSINESS_IMPORT.HEADERS.EMAIL',
    ApplicationScope: 'BUSINESS_IMPORT.HEADERS.APPLICATION',
    BusinessCategory: 'BUSINESS_IMPORT.HEADERS.CATEGORY',
    Features: 'BUSINESS_IMPORT.HEADERS.FEATURES',
    LicenseCategory: 'BUSINESS_IMPORT.HEADERS.LICENSE_CATEGORY',
    PurchaseDate: 'BUSINESS_IMPORT.HEADERS.PURCHASE_DATE',
    LicenseMonth: 'BUSINESS_IMPORT.HEADERS.LICENSE_MONTH',
    PricePerMonth: 'BUSINESS_IMPORT.HEADERS.PRICE_PER_MONTH',
    HaveEmployees: 'BUSINESS_IMPORT.HEADERS.HAVE_EMPLOYEES',
    HaveOhc: 'BUSINESS_IMPORT.HEADERS.HAVE_OHC'
  };

  step: BusinessImportStep = 'upload';
  selectedFile: File | null = null;
  rejectedFileName = '';
  preview: BusinessImportPreviewResponse | null = null;
  completion: BusinessImportConfirmResponse | null = null;
  rows: BusinessImportRow[] = [];
  loading = false;
  processing: 'validating' | 'importing' | null = null;
  fileError = '';
  apiError = '';
  failureStatus: number | null = null;
  failureReference = '';
  failureTime: Date | null = null;
  referenceCopied = false;
  dragActive = false;
  elapsedSeconds = 0;
  startedAt: Date | null = null;
  rowsSent = 0;
  jobId: string | null = null;
  jobProgress: BusinessImportConfirmResponse | null = null;
  recentImports: BusinessImportConfirmResponse[] = [];
  pollError = '';
  resultOffset = 0;
  resultsLoading = false;
  reportLoading = false;

  previewFilter: PreviewFilter = 'all';
  resultFilter: ResultFilter = 'all';
  search = '';
  showOptionalColumns = false;
  showResultRows = false;
  expandedRows = new Set<number>();
  lastRemoved: { row: BusinessImportRow; index: number } | null = null;

  private readonly editedFields = new Map<number, Set<BusinessImportField>>();
  private readonly maxFileSizeBytes = 10 * 1024 * 1024;
  private readonly subscriptions: Subscription[] = [];
  private elapsedTimer: ReturnType<typeof setInterval> | null = null;
  private undoTimer: ReturnType<typeof setTimeout> | null = null;
  private leaveConfirmed = false;
  private polling: Subscription | null = null;
  private resultRequest: Subscription | null = null;

  constructor(
    private businessServices: BusinessServices,
    private confirmationService: ConfirmationService,
    private translate: TranslateService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.loadRecentImports();
    const jobId = this.route.snapshot.queryParamMap.get('importJob');
    if (jobId !== null && /^[0-9a-f-]{36}$/i.test(jobId)) {
      this.resumeImport(jobId);
    }
  }

  ngOnDestroy(): void {
    if (this.polling !== null) this.polling.unsubscribe();
    if (this.resultRequest !== null) this.resultRequest.unsubscribe();
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.stopElapsedTimer();
    this.clearUndo();
  }

  // ---------- Upload ----------

  openFilePicker(): void {
    if (!this.loading) {
      this.fileInput?.nativeElement.click();
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length > 0 ? input.files[0] : null;
    input.value = '';
    if (file) {
      this.selectFile(file);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    if (!this.loading) {
      this.dragActive = true;
    }
  }

  onDragLeave(): void {
    this.dragActive = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragActive = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.selectFile(file);
    }
  }

  removeSelectedFile(): void {
    if (this.loading) {
      return;
    }

    this.selectedFile = null;
    this.fileError = '';
    this.apiError = '';
    this.rejectedFileName = '';
  }

  previewImport(): void {
    if (this.loading) {
      return;
    }

    if (!this.selectedFile) {
      this.fileError = this.translate.instant(
        'BUSINESS_IMPORT.ERRORS.SELECT_FILE'
      );
      return;
    }

    this.startProcessing('validating');
    this.apiError = '';
    this.subscriptions.push(
      this.businessServices.previewImport(this.selectedFile).subscribe({
        next: response => {
          this.stopProcessing();
          this.preview = response;
          this.rows = response.Rows.map(row => ({ ...row, IsSelected: true }));
          this.editedFields.clear();
          this.expandedRows.clear();
          this.rows
            .filter(row =>
              this.optionalDefaultFields.some(
                field => this.fieldErrors(row, field).length > 0
              )
            )
            .forEach(row => this.expandedRows.add(row.RowNumber));
          this.search = '';
          this.previewFilter = this.attentionCount > 0 ? 'attention' : 'all';
          this.goTo('preview');
        },
        error: error => {
          this.stopProcessing();
          this.apiError = this.getErrorMessage(error);
          this.focusHeading();
        }
      })
    );
  }

  // ---------- Review ----------

  onValueChange(row: BusinessImportRow, field: BusinessImportField): void {
    const fields =
      this.editedFields.get(row.RowNumber) ?? new Set<BusinessImportField>();
    fields.add(field);
    this.editedFields.set(row.RowNumber, fields);
  }

  isEdited(row: BusinessImportRow): boolean {
    return (this.editedFields.get(row.RowNumber)?.size ?? 0) > 0;
  }

  /** Errors to show under a field: the server's until the admin edits it, then client checks. */
  fieldErrors(row: BusinessImportRow, field: BusinessImportField): string[] {
    if (this.editedFields.get(row.RowNumber)?.has(field)) {
      const error = this.clientError(row.Values, field);
      return error ? [this.translate.instant(error)] : [];
    }

    return row.Errors.filter(error => this.fieldOf(error) === field);
  }

  /** Server errors that do not belong to one editable field. */
  generalErrors(row: BusinessImportRow): string[] {
    return row.Errors.filter(error => this.fieldOf(error) === null);
  }

  errorCount(row: BusinessImportRow): number {
    const edited = this.editedFields.get(row.RowNumber);
    if (edited === undefined || edited.size === 0) {
      return row.Errors.length;
    }

    // Keep untouched server errors; only edited fields need client validation.
    const serverErrors = row.Errors.filter(error => {
      const field = this.fieldOf(error);
      return field === null || !edited.has(field);
    }).length;
    return (
      serverErrors +
      [...edited].filter(field => this.clientError(row.Values, field) !== null)
        .length
    );
  }

  rowState(row: BusinessImportRow): 'error' | 'edited' | 'warn' | 'ok' {
    if (this.errorCount(row) > 0) {
      return 'error';
    }
    if (this.isEdited(row)) {
      return 'edited';
    }
    return row.Warnings.length > 0 ? 'warn' : 'ok';
  }

  toggleRow(row: BusinessImportRow): void {
    if (this.expandedRows.has(row.RowNumber)) {
      this.expandedRows.delete(row.RowNumber);
    } else {
      this.expandedRows.add(row.RowNumber);
    }
  }

  removeRow(row: BusinessImportRow): void {
    if (this.loading) {
      return;
    }

    const index = this.rows.indexOf(row);
    if (index < 0) {
      return;
    }

    this.rows = this.rows.filter(item => item !== row);
    this.expandedRows.delete(row.RowNumber);
    this.clearUndo();
    this.lastRemoved = { row, index };
    this.undoTimer = setTimeout(
      () => (this.lastRemoved = null),
      UNDO_TIMEOUT_MS
    );
  }

  undoRemove(): void {
    if (!this.lastRemoved || this.loading) {
      return;
    }

    const rows = [...this.rows];
    rows.splice(
      Math.min(this.lastRemoved.index, rows.length),
      0,
      this.lastRemoved.row
    );
    this.rows = rows;
    this.clearUndo();
  }

  setPreviewFilter(filter: PreviewFilter): void {
    this.previewFilter = filter;
  }

  get filteredRows(): BusinessImportRow[] {
    const term = this.search.trim().toLowerCase();
    return this.rows.filter(row => {
      const matchesFilter =
        this.previewFilter === 'all' ||
        (this.previewFilter === 'attention' && this.rowState(row) !== 'ok') ||
        (this.previewFilter === 'errors' && this.rowState(row) === 'error') ||
        (this.previewFilter === 'warnings' && row.Warnings.length > 0) ||
        (this.previewFilter === 'edited' && this.isEdited(row));
      if (!matchesFilter) {
        return false;
      }
      if (!term) {
        return true;
      }
      const { OrganizationNumber, CompanyName, Email } = row.Values;
      return [OrganizationNumber, CompanyName, Email].some(value =>
        (value ?? '').toLowerCase().includes(term)
      );
    });
  }

  get totalCount(): number {
    return this.rows.length;
  }

  get errorRowCount(): number {
    return this.rows.filter(row => this.rowState(row) === 'error').length;
  }

  get validRowCount(): number {
    return this.totalCount - this.errorRowCount;
  }

  get warningRowCount(): number {
    return this.rows.filter(row => row.Warnings.length > 0).length;
  }

  get editedRowCount(): number {
    return this.rows.filter(row => this.isEdited(row)).length;
  }

  get attentionCount(): number {
    return this.rows.filter(row => this.rowState(row) !== 'ok').length;
  }

  chooseAnotherFile(): void {
    if (this.loading) {
      return;
    }

    this.resetToUpload();
    this.goTo('upload');
  }

  confirmImport(): void {
    if (this.loading || !this.selectedFile || this.rows.length === 0) {
      return;
    }

    this.confirmationService.confirm({
      header: this.translate.instant('CONFIRMATION'),
      message: this.translate.instant('BUSINESS_IMPORT.CONFIRM_MESSAGE', {
        count: this.totalCount,
        valid: this.validRowCount
      }),
      accept: () => this.executeImport()
    });
  }

  // ---------- Results ----------

  get outcome(): 'success' | 'partial' | 'none' {
    if (!this.completion) {
      return 'none';
    }
    const issues = this.completion.FailedRows + this.completion.SkippedRows;
    if (issues === 0) {
      return 'success';
    }
    return this.completion.CreatedRows > 0 ? 'partial' : 'none';
  }

  get resultRows(): BusinessImportRow[] {
    const rows = this.completion?.Rows ?? [];
    if (this.completion !== null && this.completion.JobId) return rows;
    if (this.resultFilter === 'created') {
      return rows.filter(row => this.isStatus(row, 'Created'));
    }
    if (this.resultFilter === 'attention') {
      return rows.filter(row => !this.isStatus(row, 'Created'));
    }
    return rows;
  }

  get resultAttentionCount(): number {
    if (this.completion !== null && this.completion.JobId)
      return this.completion.FailedRows + this.completion.SkippedRows;
    return (this.completion?.Rows ?? []).filter(
      row => !this.isStatus(row, 'Created')
    ).length;
  }

  resultKind(row: BusinessImportRow): 'created' | 'skipped' | 'failed' {
    if (this.isStatus(row, 'Created')) {
      return 'created';
    }
    return this.isStatus(row, 'Skipped') ? 'skipped' : 'failed';
  }

  statusKey(status: string): string {
    return `BUSINESS_IMPORT.STATUS.${(status || 'PENDING').toUpperCase()}`;
  }

  downloadEvidenceReport(): void {
    const jobId = this.completion === null ? null : this.completion.JobId;
    if (jobId) {
      if (this.reportLoading) return;
      this.reportLoading = true;
      this.apiError = '';
      this.subscriptions.push(
        this.businessServices.importReport(jobId).subscribe({
          next: blob => {
            this.reportLoading = false;
            this.downloadBlob(blob, `business-import-${jobId}.xlsx`);
          },
          error: () => {
            this.reportLoading = false;
            this.apiError = this.translate.instant(
              'BUSINESS_IMPORT.JOB.REPORT_FAILED'
            );
          }
        })
      );
      return;
    }
    if (!this.completion?.EvidenceReportBase64) {
      return;
    }

    const byteCharacters = atob(this.completion.EvidenceReportBase64);
    const bytes = new Uint8Array(byteCharacters.length);
    for (let index = 0; index < byteCharacters.length; index++) {
      bytes[index] = byteCharacters.charCodeAt(index);
    }

    const blob = new Blob([bytes], {
      type: this.completion.EvidenceReportContentType
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = this.completion.EvidenceReportFileName;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  importAnother(): void {
    this.resetToUpload();
    this.completion = null;
    this.goTo('upload');
  }

  backToReview(): void {
    this.apiError = '';
    this.goTo('preview');
  }

  copyReference(): void {
    if (!this.failureReference || !navigator.clipboard) {
      return;
    }

    navigator.clipboard.writeText(this.failureReference).then(
      () => (this.referenceCopied = true),
      () => (this.referenceCopied = false)
    );
  }

  get businessListUrl(): string {
    return this.router.serializeUrl(
      this.router.createUrlTree(['..'], { relativeTo: this.route })
    );
  }

  goToBusinesses(): void {
    this.router.navigate(['..'], { relativeTo: this.route });
  }

  // ---------- Stepper ----------

  stepStates(): StepState[] {
    if (this.processing === 'validating') {
      return ['busy', 'upcoming', 'upcoming'];
    }
    if (this.processing === 'importing') {
      return ['done', 'busy', 'upcoming'];
    }
    if (this.step === 'upload') {
      return ['current', 'upcoming', 'upcoming'];
    }
    if (this.step === 'preview') {
      return ['done', 'current', 'upcoming'];
    }
    if (this.step === 'failed') {
      return ['done', 'done', 'failed'];
    }
    return ['done', 'done', this.outcome === 'success' ? 'done' : 'warn'];
  }

  /** Index of the step the compact (phone) stepper names. */
  get activeStepIndex(): number {
    const states = this.stepStates();
    const index = states.findIndex(state => state !== 'done');
    return index < 0 ? states.length - 1 : index;
  }

  trackRow(_: number, row: BusinessImportRow): number {
    return row.RowNumber;
  }

  get elapsedLabel(): string {
    const minutes = Math.floor(this.elapsedSeconds / 60);
    const seconds = this.elapsedSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  // ---------- Leaving the page ----------

  /** Used by the route's canDeactivate guard. */
  canDeactivate(): boolean | Observable<boolean> {
    if (this.jobProgress !== null && this.jobProgress.JobId) return true;
    const key = this.loading
      ? 'BUSINESS_IMPORT.LEAVE.PROCESSING'
      : this.step === 'preview' || this.step === 'failed'
        ? 'BUSINESS_IMPORT.LEAVE.REVIEW'
        : null;
    if (!key || this.leaveConfirmed) {
      return true;
    }

    return new Observable<boolean>(subscriber => {
      this.confirmationService.confirm({
        header: this.translate.instant('CONFIRMATION'),
        message: this.translate.instant(key),
        accept: () => {
          this.leaveConfirmed = true;
          subscriber.next(true);
          subscriber.complete();
        },
        reject: () => {
          subscriber.next(false);
          subscriber.complete();
        }
      });
    });
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.jobProgress !== null && this.jobProgress.JobId) return;
    if (this.loading || this.step === 'preview' || this.step === 'failed') {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  // ---------- Private ----------

  get processedRows(): number {
    const progress = this.jobProgress;
    return progress === null
      ? 0
      : progress.CreatedRows + progress.FailedRows + progress.SkippedRows;
  }

  get progressPercent(): number {
    const total = this.jobProgress === null ? 0 : this.jobProgress.TotalRows;
    return total > 0
      ? Math.min(100, Math.floor((this.processedRows * 100) / total))
      : 0;
  }

  resumeImport(jobId: string): void {
    if (jobId.length === 0) return;
    if (this.jobId !== jobId) {
      this.jobProgress = null;
      this.selectedFile = null;
      this.rows = [];
    }
    this.jobId = jobId;
    this.apiError = '';
    this.pollError = '';
    this.startProcessing('importing');
    this.saveJobUrl(jobId);
    this.startPolling();
  }

  retryFailedRows(): void {
    const jobId = this.completion === null ? null : this.completion.JobId;
    if (!jobId || this.loading) return;
    this.confirmationService.confirm({
      header: this.translate.instant('CONFIRMATION'),
      message: this.translate.instant('BUSINESS_IMPORT.JOB.RETRY_CONFIRM'),
      accept: () => {
        this.startProcessing('importing');
        this.subscriptions.push(
          this.businessServices.retryImport(jobId).subscribe({
            next: () => this.resumeImport(jobId),
            // A lost retry response is recovered by reading the same job, never by resubmitting the file.
            error: () => this.resumeImport(jobId)
          })
        );
      }
    });
  }

  changeResultFilter(filter: ResultFilter): void {
    this.resultFilter = filter;
    this.resultOffset = 0;
    this.loadResultPage();
  }

  onResultPage(event: { first?: number; rows?: number }): void {
    this.resultOffset = event.first ?? 0;
    this.loadResultPage(event.rows ?? 25);
  }

  loadResultPage(size = 25): void {
    const jobId = this.completion === null ? null : this.completion.JobId;
    if (!jobId) return;
    if (this.resultRequest !== null) this.resultRequest.unsubscribe();
    this.resultsLoading = true;
    this.apiError = '';
    this.resultRequest = this.businessServices
      .getImport(jobId, this.resultOffset, size, this.resultFilter)
      .subscribe({
        next: response => {
          this.resultsLoading = false;
          if (response.Status !== 'Completed') {
            this.resumeImport(jobId);
            return;
          }
          this.completion = response;
        },
        error: () => {
          this.resultsLoading = false;
          this.apiError = this.translate.instant(
            'BUSINESS_IMPORT.JOB.RESULTS_FAILED'
          );
        }
      });
  }

  private loadRecentImports(): void {
    this.subscriptions.push(
      this.businessServices.recentImports().subscribe({
        next: imports => (this.recentImports = imports),
        error: error => (this.apiError = this.getErrorMessage(error))
      })
    );
  }

  private ensureJobId(): string {
    if (this.jobId === null) this.jobId = crypto.randomUUID();
    this.saveJobUrl(this.jobId);
    return this.jobId;
  }

  private saveJobUrl(jobId: string): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { importJob: jobId },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private startPolling(): void {
    const jobId = this.jobId;
    if (jobId === null) return;
    if (this.polling !== null) this.polling.unsubscribe();
    this.polling = timer(0, 3000)
      .pipe(
        exhaustMap(() =>
          this.businessServices.getImport(jobId).pipe(
            catchError(error => {
              this.pollError = this.getErrorMessage(error);
              if ([401, 403, 404].includes(error.status)) {
                this.stopProcessing();
                this.apiError = this.pollError;
                this.failureStatus = error.status;
                this.failureReference = jobId;
                this.failureTime = new Date();
                this.goTo('failed');
              }
              return of(null);
            })
          )
        )
      )
      .subscribe(response => {
        if (response !== null) {
          this.pollError = '';
          this.receiveJob(response);
        }
      });
  }

  private receiveJob(response: BusinessImportConfirmResponse): void {
    this.jobProgress = response;
    this.rowsSent = response.TotalRows;
    if (response.StartedAtUtc) {
      this.elapsedSeconds = Math.max(
        0,
        Math.floor((Date.now() - Date.parse(response.StartedAtUtc)) / 1000)
      );
    }
    if (response.Status !== 'Completed') return;
    this.stopProcessing();
    this.completion = response;
    this.resultOffset = 0;
    this.resultFilter = this.resultAttentionCount > 0 ? 'attention' : 'all';
    this.showResultRows = this.resultAttentionCount > 0;
    this.businessServices.clearCachedBusinessService();
    this.goTo('complete');
    this.loadResultPage();
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  private selectFile(file: File): void {
    if (this.loading) {
      return;
    }

    this.fileError = '';
    this.apiError = '';
    this.rejectedFileName = '';

    const validationError = this.validateFile(file);
    if (validationError) {
      this.selectedFile = null;
      this.rejectedFileName = file.name;
      this.fileError = validationError;
      this.focusHeading();
      return;
    }

    this.selectedFile = file;
  }

  private executeImport(): void {
    if (this.loading || !this.selectedFile || this.rows.length === 0) {
      return;
    }

    this.clearUndo();
    this.startProcessing('importing');
    this.apiError = '';
    this.rowsSent = this.rows.length;
    const request: BusinessImportConfirmRequest = {
      Rows: this.rows.map(row => ({
        RowNumber: row.RowNumber,
        IsSelected: true,
        Values: row.Values
      }))
    };

    this.subscriptions.push(
      this.businessServices
        .confirmImport(this.selectedFile, request, this.ensureJobId())
        .subscribe({
          next: response => {
            if (response.JobId) {
              this.receiveJob(response);
              if (response.Status !== 'Completed') this.startPolling();
              return;
            }
            this.stopProcessing();
            this.completion = response;
            this.resultFilter =
              this.resultAttentionCount > 0 ? 'attention' : 'all';
            this.showResultRows = this.resultAttentionCount > 0;
            this.businessServices.clearCachedBusinessService();
            this.goTo('complete');
          },
          error: error => {
            this.stopProcessing();
            this.apiError = this.getErrorMessage(error);
            this.failureStatus =
              typeof error?.status === 'number' ? error.status : null;
            this.failureReference =
              error?.error?.traceId ||
              error?.error?.TraceId ||
              this.jobId ||
              '';
            this.failureTime = new Date();
            this.referenceCopied = false;
            this.goTo('failed');
          }
        })
    );
  }

  private startProcessing(mode: 'validating' | 'importing'): void {
    this.loading = true;
    this.processing = mode;
    this.startedAt = new Date();
    this.elapsedSeconds = 0;
    this.stopElapsedTimer();
    this.elapsedTimer = setInterval(() => this.elapsedSeconds++, 1000);
  }

  private stopProcessing(): void {
    if (this.polling !== null) this.polling.unsubscribe();
    this.loading = false;
    this.processing = null;
    this.stopElapsedTimer();
  }

  private stopElapsedTimer(): void {
    if (this.elapsedTimer) {
      clearInterval(this.elapsedTimer);
      this.elapsedTimer = null;
    }
  }

  private clearUndo(): void {
    if (this.undoTimer) {
      clearTimeout(this.undoTimer);
      this.undoTimer = null;
    }
    this.lastRemoved = null;
  }

  private goTo(step: BusinessImportStep): void {
    this.step = step;
    this.focusHeading();
  }

  /** Moves focus to the new state's heading so keyboard and screen-reader users land on it. */
  private focusHeading(): void {
    setTimeout(() => this.stepHeading?.nativeElement.focus());
  }

  private resetToUpload(): void {
    if (this.polling !== null) this.polling.unsubscribe();
    if (this.resultRequest !== null) this.resultRequest.unsubscribe();
    this.jobId = null;
    this.jobProgress = null;
    this.resultOffset = 0;
    this.pollError = '';
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { importJob: null },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
    this.loadRecentImports();
    this.clearUndo();
    this.selectedFile = null;
    this.rejectedFileName = '';
    this.preview = null;
    this.rows = [];
    this.editedFields.clear();
    this.expandedRows.clear();
    this.fileError = '';
    this.apiError = '';
    this.search = '';
  }

  private isStatus(row: BusinessImportRow, status: string): boolean {
    return (row.Status || '').toLowerCase() === status.toLowerCase();
  }

  private fieldOf(error: string): BusinessImportField | null {
    const text = error.toLowerCase();
    return (
      FIELD_PREFIXES.find(([, prefix]) => text.startsWith(prefix))?.[0] ?? null
    );
  }

  private clientError(
    values: BusinessImportRowValues,
    field: BusinessImportField
  ): string | null {
    const value = (values[field] ?? '').trim();
    if (!value) {
      return this.requiredFields.includes(field)
        ? 'BUSINESS_IMPORT.CLIENT_ERRORS.REQUIRED'
        : null;
    }
    if (field === 'Email' && !EMAIL_PATTERN.test(value)) {
      return 'BUSINESS_IMPORT.CLIENT_ERRORS.EMAIL';
    }
    if (
      field === 'OrganizationNumber' &&
      !ORGANIZATION_NUMBER_PATTERN.test(value)
    ) {
      return 'BUSINESS_IMPORT.CLIENT_ERRORS.ORGANIZATION_NUMBER';
    }
    if (
      field === 'Features' &&
      !value.split(/[,;]/).some(feature => feature.trim().length > 0)
    ) {
      return 'BUSINESS_IMPORT.CLIENT_ERRORS.REQUIRED';
    }
    if (field === 'PurchaseDate') {
      const date = new Date(value + 'T00:00:00Z');
      if (
        !/^\d{4}-\d{2}-\d{2}$/.test(value) ||
        Number.isNaN(date.getTime()) ||
        date.toISOString().slice(0, 10) !== value
      ) {
        return 'BUSINESS_IMPORT.CLIENT_ERRORS.PURCHASE_DATE';
      }
    }
    if (
      field === 'PricePerMonth' &&
      (!/^\d+$/.test(value) || Number(value) > 2147483647)
    ) {
      return 'BUSINESS_IMPORT.CLIENT_ERRORS.PRICE_PER_MONTH';
    }
    if (
      field === 'LicenseMonth' &&
      !/^(0?[1-9]|1[0-2]|January|February|March|April|May|June|July|August|September|October|November|December)$/i.test(
        value
      )
    ) {
      return 'BUSINESS_IMPORT.CLIENT_ERRORS.LICENSE_MONTH';
    }
    if (
      (field === 'HaveEmployees' || field === 'HaveOhc') &&
      !/^(enabled|disabled|yes|no|true|false|1|0)$/i.test(value)
    ) {
      return 'BUSINESS_IMPORT.CLIENT_ERRORS.SETTING';
    }
    return null;
  }

  private validateFile(file: File): string {
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      return this.translate.instant('BUSINESS_IMPORT.ERRORS.FILE_TYPE');
    }
    if (file.size > this.maxFileSizeBytes) {
      return this.translate.instant('BUSINESS_IMPORT.ERRORS.FILE_SIZE');
    }
    if (file.size === 0) {
      return this.translate.instant('BUSINESS_IMPORT.ERRORS.EMPTY_FILE');
    }
    return '';
  }

  private getErrorMessage(error: any): string {
    const detail: unknown = error?.error?.detail ?? error?.error?.Detail;
    if (typeof detail === 'string' && detail.trim().length > 0) {
      return detail;
    }

    switch (error?.status) {
      case 0:
        return this.translate.instant('BUSINESS_IMPORT.ERRORS.NO_RESPONSE');
      case 408:
      case 504:
        return this.translate.instant('BUSINESS_IMPORT.ERRORS.TIMEOUT');
      case 502:
      case 503:
        return this.translate.instant('BUSINESS_IMPORT.ERRORS.UNAVAILABLE');
    }

    const body: unknown = error?.error;
    if (
      typeof body === 'string' &&
      body.trim().length > 0 &&
      !body.trimStart().startsWith('<')
    ) {
      return body;
    }

    return this.translate.instant('BUSINESS_IMPORT.ERRORS.GENERIC');
  }
}
